import { Reveal, Overline } from "@/components/Reveal";
import { useLang } from "@/i18n";
import PillBadge from "@/components/PillBadge";

/**
 * Grid of fruit-variety cards (image, name, one-line description, season/origin
 * tag). Used on the Fruit Trading page for apple and mango varieties.
 * Variety names stay in Latin (trade identifiers); tags/descriptions localise.
 */
const VarietyGrid = ({ overline, title, intro, varieties }) => {
  const { data } = useLang();

  return (
    <section className="py-16 md:py-24 bg-[var(--cream)]">
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal><Overline>{overline}</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl max-w-[20ch]">{title}</h2>
        </Reveal>
        {intro && <Reveal delay={0.1}><p className="mt-5 text-[#5f6b57] text-base md:text-lg max-w-2xl leading-relaxed">{intro}</p></Reveal>}

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {varieties.map((v, i) => {
            const dv = data.varieties?.[v.name];
            return (
              <Reveal key={v.name} delay={(i % 4) * 0.06}>
                <article data-testid={`variety-${v.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="group h-full bg-white border border-[#1d3a1f]/10 rounded-2xl overflow-hidden card-soft hover:-translate-y-1 hover:border-[#d3a53a] transition-all duration-300">
                  <div className="relative h-44 overflow-hidden">
                    <img src={v.img} alt={`${v.name} — ${v.desc}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-display font-bold text-lg text-[#1d3a1f]">{v.name}</h3>
                    </div>
                    <div className="mt-2"><PillBadge tone={v.tone}>{dv?.tag || v.tag}</PillBadge></div>
                    <p className="mt-3 text-sm text-[#5f6b57] leading-relaxed">{dv?.desc || v.desc}</p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default VarietyGrid;
